/**
 * Storey → Group tree of an IFC index for a chosen group key, and the per-tileset
 * visibility state that the Structure tab edits. Keys:
 *   's:<storeyId>'          a whole storey ('_' is the synthetic Unplaced storey)
 *   'g:<storeyId>|<value>'  one group under a storey (value of the group key)
 *   'e:<featureId>'         one element
 * Pure: no React, no Cesium.
 */
import type { IfcIndex } from './index';

export const UNPLACED_STOREY = '_';
export const DEFAULT_GROUP_KEY = 'IfcClass';
export const NO_VALUE = '(none)';
// Label of the bucket for elements outside any storey. Like NO_VALUE it is also part of keys,
// so the panel translates it only where it is shown.
export const UNPLACED_LABEL = 'Unplaced';

export type IfcTreeGroup = { key: string; value: string; count: number; featureIds: number[] };
export type IfcTreeStorey = {
	key: string;
	id: string;
	label: string;
	count: number;
	groups: IfcTreeGroup[];
};
export type IfcTree = { groupKey: string; storeys: IfcTreeStorey[] };

export type IfcVisibilityState = {
	groupKey: string;
	hidden: Set<string>;
	isolate: string | null;
};

export type CheckState = 'checked' | 'unchecked' | 'indeterminate';

export const storeyKey = (storeyId: string) => `s:${storeyId}`;
export const groupKeyOf = (storeyId: string, value: string) => `g:${storeyId}|${value}`;
export const elementKey = (featureId: number) => `e:${featureId}`;

export function initialVisibility(): IfcVisibilityState {
	return { groupKey: DEFAULT_GROUP_KEY, hidden: new Set(), isolate: null };
}

export function storeyLabel(index: IfcIndex, storeyId: string): string {
	if (storeyId === UNPLACED_STOREY) return UNPLACED_LABEL;
	const node = index.nodes.get(storeyId);
	if (!node) return storeyId;
	if (node.name.trim()) return node.name;
	return node.elevation === null ? node.kind : `${node.kind} ${node.elevation}`;
}

export function buildTree(index: IfcIndex, groupKey: string): IfcTree {
	const storeyIds = [...index.storeys, UNPLACED_STOREY];
	const unplacedSlot = storeyIds.length - 1;
	const buckets = storeyIds.map(() => new Map<string, number[]>());

	for (const element of index.elements) {
		if (element.placeholder) continue;
		const slot = index.storeyOfElement[element.featureId];
		const bucket = buckets[slot >= 0 ? slot : unplacedSlot];
		const value = element.properties[groupKey] ?? NO_VALUE;
		const list = bucket.get(value);
		if (list) list.push(element.featureId);
		else bucket.set(value, [element.featureId]);
	}

	const storeys: IfcTreeStorey[] = [];
	storeyIds.forEach((id, slot) => {
		const bucket = buckets[slot];
		if (id === UNPLACED_STOREY && bucket.size === 0) return;
		const groups: IfcTreeGroup[] = [...bucket.entries()]
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([value, featureIds]) => ({
				key: groupKeyOf(id, value),
				value,
				count: featureIds.length,
				featureIds,
			}));
		storeys.push({
			key: storeyKey(id),
			id,
			label: storeyLabel(index, id),
			count: groups.reduce((n, g) => n + g.count, 0),
			groups,
		});
	});

	return { groupKey, storeys };
}

/** Feature ids a key addresses, or null when the tree does not know the key. */
export function membersOf(tree: IfcTree, key: string): Set<number> | null {
	if (key.startsWith('e:')) return new Set([Number(key.slice(2))]);
	for (const storey of tree.storeys) {
		if (storey.key === key) return new Set(storey.groups.flatMap((g) => g.featureIds));
		for (const group of storey.groups) {
			if (group.key === key) return new Set(group.featureIds);
		}
	}
	return null;
}

/** Feature ids that must not be shown. Isolate wins over the hidden set. */
export function resolveHidden(tree: IfcTree, state: IfcVisibilityState): Set<number> {
	const hidden = new Set<number>();
	if (state.isolate) {
		const keep = membersOf(tree, state.isolate);
		if (keep) {
			for (const storey of tree.storeys) {
				for (const group of storey.groups) {
					for (const id of group.featureIds) if (!keep.has(id)) hidden.add(id);
				}
			}
			return hidden;
		}
	}
	for (const storey of tree.storeys) {
		const storeyHidden = state.hidden.has(storey.key);
		for (const group of storey.groups) {
			if (!storeyHidden && !state.hidden.has(group.key)) continue;
			for (const id of group.featureIds) hidden.add(id);
		}
	}
	for (const key of state.hidden) {
		if (key.startsWith('e:')) hidden.add(Number(key.slice(2)));
	}
	return hidden;
}

export function storeyCheckState(storey: IfcTreeStorey, state: IfcVisibilityState): CheckState {
	if (state.hidden.has(storey.key)) return 'unchecked';
	const hiddenGroups = storey.groups.filter((g) => state.hidden.has(g.key)).length;
	if (hiddenGroups === 0) return 'checked';
	return hiddenGroups === storey.groups.length ? 'unchecked' : 'indeterminate';
}

export function toggleStorey(state: IfcVisibilityState, storey: IfcTreeStorey): IfcVisibilityState {
	const hidden = new Set(state.hidden);
	if (storeyCheckState(storey, state) === 'checked') {
		hidden.add(storey.key);
	} else {
		hidden.delete(storey.key);
		storey.groups.forEach((g) => hidden.delete(g.key));
	}
	return { ...state, hidden };
}

export function toggleGroup(
	state: IfcVisibilityState,
	storey: IfcTreeStorey,
	group: IfcTreeGroup
): IfcVisibilityState {
	const hidden = new Set(state.hidden);
	if (hidden.has(storey.key)) {
		// Whole storey was off: switch this group on, keep the others off.
		hidden.delete(storey.key);
		storey.groups.forEach((g) => {
			if (g.key !== group.key) hidden.add(g.key);
		});
	} else if (hidden.has(group.key)) {
		hidden.delete(group.key);
	} else {
		hidden.add(group.key);
	}
	return { ...state, hidden };
}

export function toggleIsolate(state: IfcVisibilityState, key: string): IfcVisibilityState {
	return { ...state, isolate: state.isolate === key ? null : key };
}

export function hideElement(state: IfcVisibilityState, featureId: number): IfcVisibilityState {
	const hidden = new Set(state.hidden);
	hidden.add(elementKey(featureId));
	return { ...state, hidden };
}

/** Group keys refer to the old grouping, so they are dropped; storey and element keys stay. */
export function setGroupKey(state: IfcVisibilityState, groupKey: string): IfcVisibilityState {
	if (groupKey === state.groupKey) return state;
	const hidden = new Set([...state.hidden].filter((k) => !k.startsWith('g:')));
	const isolate = state.isolate?.startsWith('g:') ? null : state.isolate;
	return { groupKey, hidden, isolate };
}

export function resetVisibility(state: IfcVisibilityState): IfcVisibilityState {
	return { groupKey: state.groupKey, hidden: new Set(), isolate: null };
}
